import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import TripChatRoom from "@/components/chat/TripChatRoom";
import { normalizeSenderRole } from "@/components/chat/tripChatRoomUtils";
import { API_BASE_URL as API } from "@/apiConfig";

export default function TemporaryDriverTripChatPage() {
  const currentRole = "driver";
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [language, setLanguage] = useState(searchParams.get("lang") || "en");
  const [chatData, setChatData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [notificationNotice, setNotificationNotice] = useState("");
  const latestIncomingMessageIdRef = useRef("");

  const updateIncomingMessageNotice = useCallback((nextChatData) => {
    const messages = nextChatData?.messages || [];
    const latestIncoming = [...messages].reverse().find((message) => (
      !message.deleted_at && normalizeSenderRole(message, nextChatData?.order) !== currentRole
    ));
    if (!latestIncoming?.id) return;
    if (latestIncomingMessageIdRef.current && latestIncomingMessageIdRef.current !== latestIncoming.id) {
      setNotificationNotice("收到新消息，当前输入内容不会被刷新清空。");
      document.title = "新消息 - 临时司机聊天室";
      if (navigator.vibrate) {
        navigator.vibrate(120);
      }
    }
    latestIncomingMessageIdRef.current = latestIncoming.id;
  }, []);

  const fetchTripChatMessages = useCallback(async (options = {}) => {
    if (!token) {
      throw new Error("临时司机聊天室链接缺少 token。");
    }
    const response = await axios.get(`${API}/trip/${orderId}/driver-chat`, {
      params: {
        token,
        lang: language,
        limit: options.limit,
        before_message_id: options.beforeMessageId,
        before_created_at: options.beforeCreatedAt,
        after_message_id: options.afterMessageId,
        after_created_at: options.afterCreatedAt,
      },
    });
    updateIncomingMessageNotice(response.data);
    return response.data;
  }, [language, orderId, token, updateIncomingMessageNotice]);

  const fetchChat = useCallback(async () => {
    if (!token) {
      setError("临时司机聊天室链接缺少 token。");
      setLoading(false);
      return;
    }
    try {
      const data = await fetchTripChatMessages();
      setChatData(data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.detail || "临时司机聊天室链接无效或已停用。");
    } finally {
      setLoading(false);
    }
  }, [fetchTripChatMessages, token]);

  useEffect(() => {
    fetchChat();
  }, [fetchChat]);

  const handleSendMessage = async (body, sourceLanguage, attachments = []) => {
    setSending(true);
    try {
      const response = await axios.post(
        `${API}/trip/${orderId}/driver-chat/messages`,
        { body, source_language: sourceLanguage, attachments },
        { params: { token } },
      );
      return response.data;
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <div className="trip-chat-luxury flex min-h-screen items-center justify-center bg-black p-6 text-[#f6d982]">Loading...</div>;
  }

  if (error) {
    return (
      <div className="trip-chat-luxury min-h-screen bg-black p-6">
        <div className="mx-auto max-w-xl rounded-2xl border border-[#d8b769]/25 bg-[#0c0a07]/90 p-4 text-[#f6d982]">
          {error}
        </div>
      </div>
    );
  }

  return (
    <TripChatRoom
      title="Temporary Driver Chat"
      chatData={chatData}
      currentRole={currentRole}
      preferredLanguage={language}
      onPreferredLanguageChange={setLanguage}
      onSendMessage={handleSendMessage}
      onFetchMessages={fetchTripChatMessages}
      notificationNotice={notificationNotice}
      sending={sending}
    />
  );
}
